import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Image as ImageIcon } from 'lucide-react';
import { aiInterviewsAPI, errMsg, fmtClock } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { fmtDate, Pill, Section, SEV_COLOR, TierPill } from '../recruiter/common';
import { RiskRing } from '../recruiter/ReviewTabs';

const DETS = [['no_concern', 'No concern', 'The signals have an innocent explanation.'], ['concern_noted', 'Concern noted', 'Worth raising in the next human round.'], ['confirmed_violation', 'Confirmed violation', 'You verified a genuine integrity breach.']];

const MediaImg = ({ id, mediaId, alt, testId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    aiInterviewsAPI.media(id, mediaId).then(({ data }) => { u = URL.createObjectURL(data); setUrl(u); }).catch(() => setUrl('error'));
    return () => u && URL.revokeObjectURL(u);
  }, [id, mediaId]);
  if (url === 'error') return <div className="iv-note is-warn">Image unavailable (it may have been deleted).</div>;
  return url ? <img src={url} alt={alt} style={{ width: '100%', borderRadius: 10 }} data-testid={testId} /> : <p className="iv-small">Loading…</p>;
};

const MediaDialog = ({ media, onClose, id }) => (
  <Dialog open={!!media} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="iv max-w-2xl" data-testid="media-dialog">
      <DialogHeader>
        <DialogTitle>{media?.title}</DialogTitle>
        <DialogDescription>{media?.biometric ? 'Biometric data. This access is recorded in the audit log.' : 'Evidence snapshot captured at the moment of the signal.'}</DialogDescription>
      </DialogHeader>
      <div className="iv-thumbs" style={{ gridTemplateColumns: `repeat(${media?.refs.length || 1}, 1fr)` }}>
        {media?.refs.map(([ref, label]) => <figure key={ref}><MediaImg id={id} mediaId={ref} alt={label} testId={`media-img-${label.toLowerCase().replace(/\s/g, '-')}`} /><figcaption>{label}</figcaption></figure>)}
      </div>
    </DialogContent>
  </Dialog>
);

function ReviewForm({ d, onDone }) {
  const [det, setDet] = useState('');
  const [notes, setNotes] = useState('');
  const [explain, setExplain] = useState(false);
  const [reinterview, setReinterview] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await aiInterviewsAPI.review(d.id, { determination: det, notes, request_explanation: explain, offer_reinterview: reinterview });
      toast.success('Integrity review saved');
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the review'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: '1rem' }} data-testid="integrity-review-form">
      <div className="iv-eyebrow" style={{ marginBottom: '0.5rem' }}>Your determination</div>
      <div className="iv-options">
        {DETS.map(([k, l, help]) => (
          <button type="button" key={k} className={`iv-option ${det === k ? 'is-on' : ''}`} onClick={() => setDet(k)} aria-pressed={det === k} data-testid={`integrity-determination-${k}`}><b>{l}</b><span>{help}</span></button>
        ))}
      </div>
      <textarea className="iv-textarea" rows={3} style={{ marginTop: '0.6rem' }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What did you check, and why this determination? (required)" data-testid="integrity-notes-input" />
      <label className="iv-row iv-small" style={{ gap: '0.4rem', marginTop: '0.5rem' }}><input type="checkbox" checked={explain} onChange={(e) => setExplain(e.target.checked)} data-testid="integrity-request-explanation" /> Ask the candidate to share context (sends a neutral email)</label>
      <label className="iv-row iv-small" style={{ gap: '0.4rem', marginTop: '0.3rem' }}><input type="checkbox" checked={reinterview} onChange={(e) => setReinterview(e.target.checked)} data-testid="integrity-offer-reinterview" /> Offer a fresh AI interview</label>
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-primary" disabled={!det || !notes.trim() || busy} onClick={submit} data-testid="integrity-submit-btn">{busy ? 'Saving…' : 'Submit review'}</button>
      </div>
    </div>
  );
}

const Signals = ({ signals, onSeek, onEvidence }) => (
  <div className="iv-timeline" data-testid="integrity-signal-timeline">
    {signals.map((s, i) => (
      <div key={s.id} className="iv-sig" style={{ '--sev': SEV_COLOR[s.severity] }} data-testid={`signal-${i}`}>
        <div className="iv-row" style={{ justifyContent: 'space-between' }}>
          <b style={{ fontSize: '0.86rem', color: '#002A38' }}>{s.label || s.type}</b>
          <span className="iv-row" style={{ gap: '0.3rem' }}>
            <Pill tone={s.severity === 'high' ? 'red' : s.severity === 'medium' ? 'amber' : s.severity === 'info' ? 'sky' : 'slate'}>{s.severity}</Pill>
            <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm iv-mono" onClick={() => onSeek({ start_ms: s.ts_ms, turn: s.turn })} data-testid={`signal-seek-${i}`}>{fmtClock(s.ts_ms)}</button>
          </span>
        </div>
        {s.details && <div className="iv-small" style={{ marginTop: 2 }}>{s.details}{s.duration_ms ? ` · ${Math.round(s.duration_ms / 1000)}s` : ''} · {s.source}</div>}
        {s.evidence_ref && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" style={{ marginTop: 4 }} onClick={() => onEvidence(s)} data-testid={`signal-evidence-btn-${i}`}><ImageIcon size={13} /> View evidence</button>}
      </div>
    ))}
  </div>
);

const IdentityChecks = ({ d, onView }) => {
  const checks = d.identity_checks || [];
  const rev = d.identity?.reverifications || [];
  if (!checks.length && d.proctoring_level === 'light') return <p className="iv-small">ID check not required for light proctoring.</p>;
  if (!checks.length) return <p className="iv-small">No ID check yet.</p>;
  const last = checks[checks.length - 1];
  return (
    <div data-testid="identity-checks">
      {checks.map((c) => (
        <div key={c.attempt} className="iv-status iv-small">
          <span>Attempt {c.attempt} · {fmtDate(c.ts)}</span>
          <span className="iv-row" style={{ gap: '0.3rem' }}>
            <Pill tone={c.result === 'pass' ? 'green' : 'amber'}>{c.result === 'pass' ? 'Passed' : 'Needs review'}</Pill>
            <span className="iv-mono">dist {c.match_distance != null ? c.match_distance.toFixed(2) : 'n/a'} / {c.threshold}</span>
            <span>liveness {c.liveness?.passed ? '✓' : '✗'}</span>
          </span>
        </div>
      ))}
      {rev.length > 0 && <p className="iv-small" style={{ marginTop: '0.4rem' }}>In-session re-checks: {rev.filter((r) => r.ok).length}/{rev.length} matched.</p>}
      {last.id_photo_ref && last.selfie_ref && (
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" style={{ marginTop: '0.5rem' }} onClick={() => onView({ title: `ID check — attempt ${last.attempt}`, biometric: true, refs: [[last.id_photo_ref, 'ID photo'], [last.selfie_ref, 'Selfie']] })} data-testid="identity-view-images-btn">
          <ImageIcon size={13} /> View ID & selfie
        </button>
      )}
    </div>
  );
};

export default function IntegrityPanel({ d, signals, onSeek, reload }) {
  const [media, setMedia] = useState(null);
  const [again, setAgain] = useState(false);
  const integ = d.integrity || {};
  const reviewed = integ.review_status === 'reviewed';
  return (
    <Section title="Integrity" testId="integrity-panel" id="integrity">
      <div className="iv-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
        <RiskRing score={integ.risk_score || 0} testId="integrity-risk-score" />
        <div>
          <TierPill tier={integ.tier?.key} testId="integrity-tier" />
          <div className="iv-small" style={{ marginTop: 4 }} data-testid="integrity-label">{integ.label}</div>
          {integ.quality_discount_applied && <div className="iv-small">Camera signals halved for poor video/network quality.</div>}
          {integ.suppressed_by_accommodation?.length > 0 && <div className="iv-small">Suppressed by accommodation: {integ.suppressed_by_accommodation.join(', ')}</div>}
        </div>
      </div>
      <p className="iv-small" style={{ marginBottom: '0.75rem' }}>0–24 clear · 25–59 review suggested · 60+ mandatory review. Signals route to a person; they never reject anyone.</p>
      {signals.length ? <Signals signals={signals} onSeek={onSeek} onEvidence={(s) => setMedia({ title: s.label, refs: [[s.evidence_ref, 'Snapshot']] })} /> : <p className="iv-small" data-testid="integrity-no-signals">No integrity signals recorded.</p>}
      <div className="iv-eyebrow" style={{ margin: '1rem 0 0.4rem' }}>Identity</div>
      <IdentityChecks d={d} onView={setMedia} />
      {integ.candidate_explanation && (
        <div className="iv-note" style={{ marginTop: '0.9rem' }} data-testid="integrity-candidate-explanation"><b>Candidate's context ({fmtDate(integ.candidate_explanation.at)}):</b> {integ.candidate_explanation.text}</div>
      )}
      {reviewed && !again && (
        <div className="iv-note is-good" style={{ marginTop: '0.9rem' }} data-testid="integrity-review-result">
          <b>{integ.label}</b> by {integ.reviewed_by} · {fmtDate(integ.reviewed_at)}<div>{integ.review_notes}</div>
          <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" style={{ marginTop: '0.5rem' }} onClick={() => setAgain(true)} data-testid="integrity-review-again-btn">Review again</button>
        </div>
      )}
      {(!reviewed || again) && ['completed', 'scoring', 'report_ready', 'score_error', 'interrupted'].includes(d.status) && <ReviewForm d={d} onDone={() => { setAgain(false); reload(); }} />}
      <MediaDialog media={media} onClose={() => setMedia(null)} id={d.id} />
    </Section>
  );
}
